/**
 * Custom Algolia hooks for search integration.
 * Registers before-query and template modification hooks for Algolia InstantSearch.
 *
 * @description This module extends Algolia's default behavior with custom functionality:
 * - Modifies facet limit numbers
 * - Customizes autocomplete product template with pricing and swatches
 * - Adds stock policy and collection filters to autocomplete searches
 *
 * @listens algolia.hooks.initialize - Event fired when Algolia is ready
 * @requires algoliaShopify (global Algolia helper object)
 * @requires algolia_custom_helpers
 */

document.addEventListener('algolia.hooks.initialize', function () {
  /**
   * Hook: beforeInstantSearchFacetLimitNumber
   * Modifies the default maximum number of facet values to display.
   *
   * @hook beforeInstantSearchFacetLimitNumber
   * @param {number} limit - The default facet limit number
   * @returns {number} Modified limit value (50)
   *
   * @example
   * // Returns 50 as new facet limit
   */
  algoliaShopify.hooks.registerHook('beforeInstantSearchFacetLimitNumber', function (limit) {
    // Modify default limit, then return a number
    return 50;
  });

  /**
   * Hook: beforeAutocompleteProductTemplate
   * Customizes the autocomplete dropdown product template with:
   * - Custom pricing from metafields
   * - Swatches count display
   * - Modified HTML structure for product cards
   *
   * @hook beforeAutocompleteProductTemplate
   * @param {Function} _defaultTemplate - Original template function
   * @param {Object} templateOptions - Options containing html, item, components
   * @param {boolean} distinct - Whether distinct mode is enabled
   * @param {string} itemLink - URL to the product page
   * @returns {string} HTML template string for autocomplete item
   *
   * @example
   * // Customizes autocomplete result display
   */
  algoliaShopify.hooks.registerHook(
    "beforeAutocompleteProductTemplate",
    function (_defaultTemplate, templateOptions, distinct, itemLink) {
      // Modify default options, then return them
      const { html, item, components } = templateOptions;

      let algoliaCustomHelpers = new algolia_custom_helpers();
      // Get custom prices from product metafields and append them to results
      algoliaCustomHelpers.getProductMetafieldsAndProcessPrices(item, algoliaShopify.config.products_autocomplete_hits_per_page, true);
      algoliaCustomHelpers.addSwatchesCountToCard(item, true);

      return html`
    <a
      href="${itemLink}"
      class="aa-ItemLink aa-ProductItem"
      onClick="${() => algoliaShopify.helpers.handleItemClick(item)}"
    >
      <div class="aa-ItemContent">
        <div class="aa-ItemPicture aa-ItemPicture--loaded">
          <img
            src="${algoliaShopify.helpers.compactImage(item)}"
            alt="${item.title}"
          />
        </div>
        <div class="aa-ItemContentBody">
            <div class="aa-ItemContentBrand">
                ${item.product_type && components.Highlight({ hit: item, attribute: 'product_type' })}
                ${item.vendor && html`<span> by </span>`}
                ${item.vendor && components.Highlight({ hit: item, attribute: 'vendor' })}
            </div>
          <div class="aa-ItemContentTitleWrapper">
            <div class="aa-ItemContentTitle">
              ${components.Highlight({ hit: item, attribute: "title" })}
              <span class="algolia-variant">
                ${algoliaShopify.helpers.variantTitleAddition(item, distinct)}
              </span>
            </div>
          </div>
            <div class="aa-ItemContentPrice custom-price">
                <div class="aa-ItemContentPriceCurrent"></div>
            </div>
          <div class="aa-ItemContentPrice">
            <div class="aa-ItemContentPriceCurrent">
              ${algoliaShopify.helpers.displayPrice(item, distinct)}
            </div>
          </div>
          <p class="ais-hit--swatches-count hidden">[[VALUE]] colors available!</p>
        </div>
      </div>
    </a>
  `;
    }
  );

  /**
   * Hook: beforeAutocompleteFiltersString
   * Builds filter string for autocomplete searches including:
   * - Stock policy filtering (deny/continue based on config)
   * - Collection filtering (allowed + wizard collections)
   * - Tag exclusion
   *
   * @hook beforeAutocompleteFiltersString
   * @param {string} _defaultFilter - Default filter string (unused)
   * @returns {string} Algolia filter string combining all conditions
   *
   * @example
   * // Returns filter string like: "inventory_quantity > 0 AND (collections:col1 OR collections:col2)"
   */
  algoliaShopify.hooks.registerHook('beforeAutocompleteFiltersString', function(_defaultFilter) {
    // Filters for stock policy
    let stockPolicyFilter = '';

    // Handle inventory-based stock policies
    if (algoliaShopify.config.stock_policy === 'deny') {
      // For 'deny' we will filter out all items based on inventory quantity
      stockPolicyFilter = 'inventory_quantity > 0';
    } else if (algoliaShopify.config.stock_policy === 'continue') {
      /**
       * For 'continue' we will filter on `inventory_available` attribute whose
       * value is dependent on:
       * `inventory_quantity > 0 OR inventory_policy == 'continue'`
       */
      stockPolicyFilter = 'inventory_available:true';
    }

    let filters = [];
    if (stockPolicyFilter){
      filters.push(stockPolicyFilter)
    }

    // Handle collection filtering
    let collectionFilters = '';

    // Because algolia doesn't like
    // A AND ((B OR C) AND D)
    // We have to do this in an annoying way
    let hasAllowedCollections = window.allowed_collections && window.allowed_collections.length > 0
    let hasWizardCollections = window.wizard_collections && window.wizard_collections.length > 0

    let allowedCollections = hasAllowedCollections ? window.allowed_collections.map(c => `collections:"${c}"`).join(' OR ') : [];

    let collections = [
      ...(hasAllowedCollections ? window.allowed_collections : []),
      ...(hasWizardCollections ? window.wizard_collections : []),
    ].map(c => `collections:"${c}"`).join(' OR ');

    if(collections && collections.length > 0) {
      collectionFilters = `(${collections})`
    }

    if (
      collectionFilters
      && hasWizardCollections
      && window.has_filter_collection
    ){
      if (collectionFilters.length > 0) {
        collectionFilters += ' AND ';
      }
      collectionFilters += `(`;

      if(allowedCollections.length > 0) {
        collectionFilters += `${allowedCollections} OR `;
      }

      collectionFilters += `collections:"${window.has_filter_collection}")`;

    }
    else if (
      !hasAllowedCollections
      && !hasWizardCollections
      && !window.is_main_store
    ){
      if (collectionFilters.length > 0) {
        collectionFilters += ' AND ';
      }
      collectionFilters += `collections:"a-non-existing-collection-to-hide-everything"`;
    }

    if (collectionFilters && collectionFilters != ""){
      filters.push(collectionFilters)
    }

    if(typeof window.exclude_tag != "undefined" && window.exclude_tag.length > 0) {
      filters.push(`NOT tags:"${window.exclude_tag}"`)
    }
    return filters.join(' AND ');
  });
});